/* CallingPerformanceReport.jsx
 *
 * Admin + Sales Manager view of the existing SA Calling Data cards.
 * Reuses `CallingWorkspaceHome` (tiles UI) and the /sa/calling-overview API —
 * no duplicate reporting logic. Backend enforces team/SA scoping.
 */
import { useEffect, useMemo, useState } from "react";
import { http, notify } from "../api";
import { Loading } from "./common";
import { CallingWorkspaceHome } from "./CallingWorkspace";

export default function CallingPerformanceReport({ user }) {
  const [scope, setScope] = useState(null);        // { teams, sales_associates }
  const [teamId, setTeamId] = useState("");         // "" = all teams
  const [saId, setSaId] = useState("");             // "" = all SAs in current scope
  const [campaigns, setCampaigns] = useState([]);
  const [campaignId, setCampaignId] = useState(""); // "" = all campaigns
  const [campaignDate, setCampaignDate] = useState("");
  const [overview, setOverview] = useState(null);
  const [detail, setDetail] = useState(null);      // when a card is clicked

  const isManager = user?.role === "sales_manager";
  const isAdmin = user?.role === "admin" || user?.role === "business_head" || user?.role === "sub_admin";

  // Load dropdown data once.
  useEffect(() => {
    http.get("/sa/performance-scope")
      .then((r) => setScope(r.data))
      .catch((e) => notify.error(e, "Could not load teams / SA list"));
    http.get("/sa/available-campaigns")
      .then((r) => setCampaigns(r.data.campaigns || []))
      .catch(() => setCampaigns([]));
  }, []);

  // Filter SA options by selected team (or by manager's own team).
  const saOptions = useMemo(() => {
    if (!scope) return [];
    if (isManager) return scope.sales_associates;   // already scoped by backend
    if (!teamId) return scope.sales_associates;
    return scope.sales_associates.filter((s) => s.team_id === teamId);
  }, [scope, teamId, isManager]);
  const dateOptions = useMemo(() => {
    const s = new Set();
    for (const c of campaigns) {
      if (!campaignId || c.campaign_id === campaignId) {
        if (c.campaign_date) s.add(c.campaign_date);
      }
    }
    return Array.from(s).sort().reverse();
  }, [campaigns, campaignId]);

  // When team changes, reset SA if the current SA doesn't belong.
  useEffect(() => {
    if (saId && !saOptions.find((s) => s.id === saId)) setSaId("");
  }, [saOptions, saId]);

  // Fetch overview whenever filters change.
  useEffect(() => {
    const qs = new URLSearchParams();
    if (saId) qs.set("sa_id", saId);
    else if (teamId) qs.set("team_id", teamId);
    if (campaignId) qs.set("campaign_id", campaignId);
    if (campaignDate) qs.set("campaign_date", campaignDate);
    setOverview(null);
    http.get(`/sa/calling-overview?${qs.toString()}`)
      .then((r) => setOverview(r.data))
      .catch((e) => notify.error(e, "Could not load performance report"));
  }, [saId, teamId, campaignId, campaignDate]);

  // Human-readable labels for each tile bucket.
  const BUCKET_TITLES = {
    assigned: "Assigned Data",
    lead_created: "Lead Created",
    sent_to_login: "Sent to Login",
    callback: "Call Back",
    pending: "Pending Data",
    follow_up: "Follow-up",
    skipped: "Skipped Lead",
    "": "All Campaign Data",
  };

  // Every tile clickable — show a filtered list for the chosen bucket.
  // `CallingWorkspaceHome` invokes onOpenBucket with a plain status string
  // (e.g. "assigned", "callback", "lead_created", "" for all).
  const scopeQs = () => {
    const qs = new URLSearchParams();
    if (saId) qs.set("sa_id", saId);
    else if (teamId) qs.set("team_id", teamId);
    if (campaignId) qs.set("campaign_id", campaignId);
    if (campaignDate) qs.set("campaign_date", campaignDate);
    return qs;
  };
  const onOpenBucket = (bucketStatus) => {
    const qs = scopeQs();
    if (bucketStatus) qs.set("status", bucketStatus);
    setDetail({
      type: "records",
      title: BUCKET_TITLES[bucketStatus ?? ""] ?? "Records",
      qs: qs.toString(),
    });
  };
  const onOpenScripts = () => setDetail({ type: "scripts", title: "Calling Scripts" });
  const onOpenHistory = () => setDetail({ type: "history", title: "Calling History", qs: scopeQs().toString() });
  const onNavigate = (key) => {
    if (key === "my_followups") setDetail({ type: "followups", title: "My Follow-ups", qs: scopeQs().toString() });
    else if (key === "policy_finder") setDetail({ type: "policy", title: "Bank Policy & EMI" });
  };

  if (scope === null) return <Loading>Loading dropdowns…</Loading>;

  const contextLabel = (() => {
    if (saId) return `SA: ${(saOptions.find((s) => s.id === saId) || {}).name || "—"}`;
    if (teamId) return `Team: ${(scope.teams.find((t) => t.team_id === teamId) || {}).name || teamId}`;
    return isManager ? "My entire team" : "All teams";
  })();

  return (
    <section data-testid="calling-performance-report">
      {/* Filter bar */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 14, alignItems: "center" }}>
        {isAdmin && (
          <label>
            <span className="eyebrow" style={{ marginRight: 6 }}>MANAGER / TEAM</span>
            <select value={teamId} onChange={(e) => setTeamId(e.target.value)} data-testid="perf-team-select">
              <option value="">All Teams</option>
              {scope.teams.map((t) => (
                <option key={t.team_id} value={t.team_id}>{t.name}</option>
              ))}
            </select>
          </label>
        )}
        {(!isAdmin || teamId) && (
          <label>
            <span className="eyebrow" style={{ marginRight: 6 }}>{isManager ? "TEAM MEMBER" : "SALES ASSOCIATE"}</span>
            <select value={saId} onChange={(e) => setSaId(e.target.value)} data-testid="perf-sa-select">
              <option value="">All Sales Associates</option>
              {saOptions.map((s) => (
                <option key={s.id} value={s.id}>{s.name}{s.employee_code ? ` · ${s.employee_code}` : ""}</option>
              ))}
            </select>
          </label>
        )}
        <label>
          <span className="eyebrow" style={{ marginRight: 6 }}>CAMPAIGN</span>
          <select value={campaignId}
            onChange={(e) => { setCampaignId(e.target.value); setCampaignDate(""); }}
            data-testid="perf-campaign-select">
            <option value="">All Campaigns</option>
            {campaigns.map((c) => (
              <option key={c.campaign_id} value={c.campaign_id}>
                {c.campaign_id} · {c.name}
              </option>
            ))}
          </select>
        </label>
        {dateOptions.length > 0 && (
          <label>
            <span className="eyebrow" style={{ marginRight: 6 }}>CAMPAIGN DATE</span>
            <select value={campaignDate}
              onChange={(e) => setCampaignDate(e.target.value)}
              data-testid="perf-campaign-date-select">
              <option value="">All Dates</option>
              {dateOptions.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </label>
        )}
        <span className="muted" data-testid="perf-context">Showing: <strong>{contextLabel}</strong></span>
      </div>

      {!overview ? (
        <Loading>Computing report…</Loading>
      ) : (
        <CallingWorkspaceHome
          overview={overview}
          onOpenBucket={onOpenBucket}
          onOpenScripts={onOpenScripts}
          onOpenHistory={onOpenHistory}
          onNavigate={onNavigate}
          onCreateLead={() => { }}
          hideCreateLead
          title="Data Calling Performance"
          eyebrow={isManager ? "SALES MANAGER" : "ADMIN"}
          subtitle="Reuses the same calling data your Sales Associates see — click any card to drill in."
        />
      )}

      {detail && <CardModal detail={detail} onClose={() => setDetail(null)} />}
    </section>
  );
}

function CardModal({ detail, onClose }) {
  const stopBubble = (e) => e.stopPropagation();
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60 }} onClick={onClose}>
      <div
        style={{ background: "#fff", padding: 20, borderRadius: 12, width: "100%", maxWidth: 860, maxHeight: "90vh", overflow: "auto" }}
        onClick={stopBubble}
        data-testid={`perf-card-modal-${detail.type}`}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ margin: 0 }}>{detail.title}</h3>
          <button className="outline-button compact" onClick={onClose} data-testid="perf-detail-close">Close</button>
        </div>
        <div style={{ marginTop: 12 }}>
          {detail.type === "records"   && <RecordsCard qs={detail.qs} />}
          {detail.type === "followups" && <FollowupsCard qs={detail.qs} />}
          {detail.type === "history"   && <HistoryCard qs={detail.qs} />}
          {detail.type === "scripts"   && <ScriptsCard />}
          {detail.type === "policy"    && <PolicyEmiCard />}
        </div>
      </div>
    </div>
  );
}

function RecordsCard({ qs }) {
  const [rows, setRows] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  useEffect(() => {
    http.get(`/sa/calling-data?${qs}`).then((r) => setRows(r.data.customers || r.data.rows || [])).catch(() => setRows([]));
  }, [qs]);
  if (rows === null) return <Loading>Loading…</Loading>;
  if (!rows.length) return <div className="empty">No records for this bucket in the current selection.</div>;
  return (
    <>
      <table className="records-table" data-testid="perf-detail-table">
        <thead><tr><th>Customer</th><th>Mobile</th><th>Status</th><th>Assigned</th><th>Last called</th></tr></thead>
        <tbody>
          {rows.map((c, i) => {
            const cid = c.customer_id || c.lead_id || "";
            return (
              <tr
                key={cid || i}
                style={{ cursor: cid ? "pointer" : "default" }}
                onClick={() => cid && setSelectedId(cid)}
                data-testid={`perf-row-${cid || i}`}
              >
                <td>{c.customer_name || c.name || "—"}</td>
                <td>{c.mobile || "—"}</td>
                <td>{c.status || c.last_disposition || "—"}</td>
                <td>{c.assigned_to_name || c.sa_name || "—"}</td>
                <td>{(c.last_call_at || c.assigned_at || "").slice(0, 16).replace("T", " ")}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {selectedId && <CustomerDetailModal customerId={selectedId} onClose={() => setSelectedId(null)} />}
    </>
  );
}

function FollowupsCard({ qs }) {
  const [rows, setRows] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  useEffect(() => {
    const q = qs ? `${qs}&bucket=all` : "bucket=all";
    http.get(`/sa/followups?${q}`).then((r) => setRows(r.data.followups || [])).catch(() => setRows([]));
  }, [qs]);
  if (rows === null) return <Loading>Loading follow-ups…</Loading>;
  if (!rows.length) return <div className="empty">No follow-ups in the current selection.</div>;
  return (
    <>
      <table className="records-table" data-testid="perf-followups-table">
        <thead><tr><th>Due</th><th>Customer</th><th>Mobile</th><th>Loan</th><th>Status</th><th>Notes</th></tr></thead>
        <tbody>
          {rows.map((f, i) => {
            const cid = f.customer_id || "";
            return (
              <tr
                key={f.followup_id || i}
                style={{ cursor: cid ? "pointer" : "default" }}
                onClick={() => cid && setSelectedId(cid)}
                data-testid={`perf-fu-${f.followup_id || i}`}
              >
                <td>{(f.due_at || "").slice(0, 16).replace("T", " ")}</td>
                <td>{f.customer_name || "—"}</td>
                <td>{f.mobile || "—"}</td>
                <td>{f.loan_type || "—"} {f.loan_amount ? `· ₹${f.loan_amount}` : ""}</td>
                <td>{f.status || "—"}</td>
                <td>{f.notes || "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {selectedId && <CustomerDetailModal customerId={selectedId} onClose={() => setSelectedId(null)} />}
    </>
  );
}

function HistoryCard({ qs }) {
  const [rows, setRows] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  useEffect(() => {
    const q = qs ? `${qs}&days=30` : "days=30";
    http.get(`/sa/call-history?${q}`).then((r) => setRows(r.data.history || [])).catch(() => setRows([]));
  }, [qs]);
  if (rows === null) return <Loading>Loading history…</Loading>;
  if (!rows.length) return <div className="empty">No call history in the last 30 days.</div>;
  return (
    <>
      <table className="records-table" data-testid="perf-history-table">
        <thead><tr><th>When</th><th>Customer</th><th>Mobile</th><th>Disposition</th><th>Notes</th><th>By</th></tr></thead>
        <tbody>
          {rows.map((h, i) => {
            const cid = h.customer_id || "";
            return (
              <tr
                key={i}
                style={{ cursor: cid ? "pointer" : "default" }}
                onClick={() => cid && setSelectedId(cid)}
                data-testid={`perf-hist-${i}`}
              >
                <td>{(h.created_at || "").slice(0, 16).replace("T", " ")}</td>
                <td>{h.customer_name || "—"}</td>
                <td>{h.mobile || "—"}</td>
                <td>{h.disposition || "—"}</td>
                <td>{h.notes || "—"}</td>
                <td>{h.sa_name || h.created_by || "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {selectedId && <CustomerDetailModal customerId={selectedId} onClose={() => setSelectedId(null)} />}
    </>
  );
}

function ScriptsCard() {
  const [rows, setRows] = useState(null);
  const [openId, setOpenId] = useState(null);
  useEffect(() => {
    http.get("/sa/scripts").then((r) => setRows(r.data.scripts || [])).catch(() => setRows([]));
  }, []);
  if (rows === null) return <Loading>Loading scripts…</Loading>;
  if (!rows.length) return <div className="empty">No active scripts.</div>;
  return (
    <ul style={{ listStyle: "none", padding: 0, margin: 0 }} data-testid="perf-scripts-list">
      {rows.map((s, i) => {
        const id = s.script_id || i;
        const isOpen = openId === id;
        return (
          <li key={id} style={{ border: "1px solid #eee", borderRadius: 10, marginBottom: 8, padding: 12 }}>
            <button
              type="button"
              onClick={() => setOpenId(isOpen ? null : id)}
              style={{ background: "none", border: "none", padding: 0, cursor: "pointer", width: "100%", textAlign: "left" }}
              data-testid={`perf-script-toggle-${id}`}
            >
              <strong>{s.title || "Untitled"}</strong>
              {s.loan_type && <span className="muted" style={{ marginLeft: 8 }}>· {s.loan_type}</span>}
            </button>
            {isOpen && (
              <div style={{ marginTop: 10, whiteSpace: "pre-wrap", color: "#333" }}>
                {s.body || s.content || "(empty script)"}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function PolicyEmiCard() {
  const [tab, setTab] = useState("emi");
  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <button
          className={tab === "emi" ? "primary-button compact" : "outline-button compact"}
          onClick={() => setTab("emi")}
          data-testid="perf-tab-emi"
        >EMI Calculator</button>
        <button
          className={tab === "policy" ? "primary-button compact" : "outline-button compact"}
          onClick={() => setTab("policy")}
          data-testid="perf-tab-policy"
        >Bank Policies</button>
      </div>
      {tab === "emi" ? <EmiCalculator /> : <BankPolicies />}
    </div>
  );
}

function EmiCalculator() {
  const [principal, setPrincipal] = useState(500000);
  const [rate, setRate] = useState(10.5);
  const [months, setMonths] = useState(60);
  const p = Number(principal) || 0;
  const r = (Number(rate) || 0) / 12 / 100;
  const n = Number(months) || 0;
  const emi = r > 0 && n > 0 ? (p * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1) : 0;
  const total = emi * n;
  const interest = total - p;
  const fmt = (v) => (isFinite(v) ? v.toLocaleString("en-IN", { maximumFractionDigits: 0 }) : "—");
  return (
    <div>
      <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(3, 1fr)" }}>
        <label>
          <span className="eyebrow">Principal (₹)</span>
          <input type="number" value={principal} onChange={(e) => setPrincipal(e.target.value)} data-testid="emi-principal" />
        </label>
        <label>
          <span className="eyebrow">Rate (% / year)</span>
          <input type="number" step="0.01" value={rate} onChange={(e) => setRate(e.target.value)} data-testid="emi-rate" />
        </label>
        <label>
          <span className="eyebrow">Tenure (months)</span>
          <input type="number" value={months} onChange={(e) => setMonths(e.target.value)} data-testid="emi-months" />
        </label>
      </div>
      <div style={{ marginTop: 16, padding: 12, background: "#f8f9fb", borderRadius: 10 }} data-testid="emi-result">
        <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0" }}>
          <span className="muted">Monthly EMI</span><strong>₹ {fmt(emi)}</strong>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0" }}>
          <span className="muted">Total interest</span><strong>₹ {fmt(interest)}</strong>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0" }}>
          <span className="muted">Total payment</span><strong>₹ {fmt(total)}</strong>
        </div>
      </div>
    </div>
  );
}

function BankPolicies() {
  const [rows, setRows] = useState(null);
  const [loanType, setLoanType] = useState("");
  useEffect(() => {
    const qs = loanType ? `?loan_type=${encodeURIComponent(loanType)}` : "";
    http.get(`/policies/search${qs}`).then((r) => setRows(r.data.policies || [])).catch(() => setRows([]));
  }, [loanType]);
  return (
    <>
      <label style={{ marginBottom: 10, display: "inline-block" }}>
        <span className="eyebrow" style={{ marginRight: 6 }}>LOAN TYPE</span>
        <select value={loanType} onChange={(e) => setLoanType(e.target.value)} data-testid="policy-loan-type">
          <option value="">All</option>
          <option value="Personal Loan">Personal Loan</option>
          <option value="Business Loan">Business Loan</option>
          <option value="Home Loan">Home Loan</option>
          <option value="Loan Against Property">Loan Against Property</option>
        </select>
      </label>
      {rows === null ? <Loading>Loading policies…</Loading> : !rows.length ? (
        <div className="empty">No policies match.</div>
      ) : (
        <table className="records-table" data-testid="perf-policies-table">
          <thead><tr><th>Bank</th><th>Product</th><th>Loan Type</th><th>Min ROI</th><th>Max amt</th><th>Tenure</th></tr></thead>
          <tbody>
            {rows.map((p, i) => (
              <tr key={p.policy_id || i}>
                <td>{p.bank_name || "—"}</td>
                <td>{p.product_name || "—"}</td>
                <td>{p.loan_type || "—"}</td>
                <td>{p.min_rate ?? p.roi_min ?? "—"}</td>
                <td>{p.max_amount ?? "—"}</td>
                <td>{p.max_tenure_months ? `${p.max_tenure_months} mo` : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

function CustomerDetailModal({ customerId, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    // For "lead::LDxxx" ids (skipped-bucket synthetic rows) there is no
    // campaign_customer detail — show a lightweight message.
    if (String(customerId).startsWith("lead::")) {
      setData({ notice: "This is a Lead-only entry; open it from the Leads screen for full detail." });
      return;
    }
    http.get(`/sa/calling-data/${customerId}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ notice: "Could not load customer detail." }));
  }, [customerId]);

  const stopBubble = (e) => e.stopPropagation();
  return (
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 70 }}
      onClick={onClose}
    >
      <div
        style={{ background: "#fff", padding: 20, borderRadius: 12, width: "100%", maxWidth: 720, maxHeight: "90vh", overflow: "auto" }}
        onClick={stopBubble}
        data-testid="perf-customer-detail"
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>Customer Details</h3>
          <button className="outline-button compact" onClick={onClose} data-testid="perf-customer-close">Close</button>
        </div>
        {!data ? <Loading>Loading…</Loading> : data.notice ? (
          <div className="empty">{data.notice}</div>
        ) : (
          <CustomerDetailBody customer={data.customer} dispositions={data.dispositions} followups={data.followups} />
        )}
      </div>
    </div>
  );
}

function CustomerDetailBody({ customer, dispositions, followups }) {
  const c = customer || {};
  const kv = (k, v) => (
    <div style={{ display: "flex", gap: 12, padding: "6px 0", borderBottom: "1px dashed #eee" }}>
      <span className="muted" style={{ minWidth: 160 }}>{k}</span>
      <strong style={{ flex: 1 }}>{v || "—"}</strong>
    </div>
  );
  return (
    <div>
      <div style={{ marginBottom: 14 }}>
        {kv("Name", c.customer_name)}
        {kv("Mobile", c.mobile)}
        {kv("Loan type", c.loan_type)}
        {kv("Loan amount", c.loan_amount)}
        {kv("Campaign", c.campaign_name || c.campaign_id)}
        {kv("Assigned to", c.sa_name || c.assigned_to_name)}
        {kv("Status", c.status)}
        {kv("Last disposition", c.last_disposition)}
        {kv("Next callback", (c.next_callback_at || "").slice(0, 16).replace("T", " "))}
      </div>

      <div style={{ marginBottom: 14 }}>
        <p className="eyebrow" style={{ marginBottom: 6 }}>DISPOSITION HISTORY</p>
        {(!dispositions || dispositions.length === 0) ? (
          <div className="empty" style={{ padding: 8 }}>No dispositions logged.</div>
        ) : (
          <table className="records-table">
            <thead><tr><th>When</th><th>Disposition</th><th>Notes</th><th>By</th></tr></thead>
            <tbody>
              {dispositions.map((d, i) => (
                <tr key={i}>
                  <td>{(d.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td>{d.disposition || "—"}</td>
                  <td>{d.notes || "—"}</td>
                  <td>{d.sa_name || d.created_by || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div>
        <p className="eyebrow" style={{ marginBottom: 6 }}>FOLLOW-UPS</p>
        {(!followups || followups.length === 0) ? (
          <div className="empty" style={{ padding: 8 }}>No follow-ups scheduled.</div>
        ) : (
          <table className="records-table">
            <thead><tr><th>Due</th><th>Status</th><th>Notes</th></tr></thead>
            <tbody>
              {followups.map((f, i) => (
                <tr key={i}>
                  <td>{(f.due_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td>{f.status || "—"}</td>
                  <td>{f.notes || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
